// client/src/coms/stickers/localStickerStore.js
// Local sticker packs: stored on this device only (IndexedDB, like the soundboard's local clips in
// soundboard/soundStore.js), free, not shareable, never in the Marketplace. Falls back to memory when
// IndexedDB is unavailable (private windows).
//
// Other people never read this store. When a local sticker is sent, only that one image is uploaded to the
// sender's private relay pack (localStickerRelay.js); "Move to Voxemera Server" uploads a whole pack as a
// normal server-hosted pack. Images are kept as prepared (transparency and animation preserved) and must
// meet the server's limits, so a local pack can always be moved to the server later.

import { notifyStickersChanged } from './stickerSuggest';

const DB_NAME = 'voxemera-stickers';
const DB_VERSION = 1;
const PACKS = 'packs'; // { id, name, description, cover_sticker_id, position, created_at, updated_at, server_pack_id, moved_at }
const STICKERS = 'stickers'; // { id, pack_id, name, keywords, blob, mime, animated, favorite, position, created_at, updated_at, relay }

export const LOCAL_LIMITS = { maxPacks: 30, maxStickersPerPack: 60, maxBytes: 2 * 1024 * 1024 };
export const LOCAL_ID_PREFIX = 'local:';

let dbPromise = null;
const memory = { [PACKS]: new Map(), [STICKERS]: new Map() };
const urls = new Map(); // sticker id -> { url, blob }

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(PACKS)) db.createObjectStore(PACKS, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(STICKERS)) {
          const os = db.createObjectStore(STICKERS, { keyPath: 'id' });
          os.createIndex('pack_id', 'pack_id', { unique: false });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

const req2p = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

async function tx(store, mode, fn) {
  const db = await openDb();
  if (!db) return fn(null);
  return new Promise((resolve, reject) => {
    let result;
    const t = db.transaction(store, mode);
    Promise.resolve(fn(t.objectStore(store))).then((r) => {
      result = r;
    }, reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

const getAll = (store) => tx(store, 'readonly', (os) => (os ? req2p(os.getAll()) : Array.from(memory[store].values())));
const getOne = (store, key) => tx(store, 'readonly', (os) => (os ? req2p(os.get(key)) : memory[store].get(key)));
const putOne = (store, value) => tx(store, 'readwrite', (os) => (os ? req2p(os.put(value)) : memory[store].set(value.id, value)));
const removeOne = (store, key) => tx(store, 'readwrite', (os) => (os ? req2p(os.delete(key)) : memory[store].delete(key)));

function newId(prefix) {
  const rand = typeof crypto !== 'undefined' && crypto.getRandomValues ? Array.from(crypto.getRandomValues(new Uint8Array(8)), (b) => b.toString(16).padStart(2, '0')).join('') : Math.random().toString(16).slice(2, 18);
  return `${prefix}_${Date.now().toString(36)}${rand}`;
}

function changed() {
  notifyStickersChanged();
}

function urlFor(sticker) {
  const hit = urls.get(sticker.id);
  if (hit && hit.blob === sticker.blob) return hit.url;
  if (hit) URL.revokeObjectURL(hit.url);
  const url = URL.createObjectURL(sticker.blob);
  urls.set(sticker.id, { url, blob: sticker.blob });
  return url;
}

function forget(stickerId) {
  const hit = urls.get(stickerId);
  if (hit) URL.revokeObjectURL(hit.url);
  urls.delete(stickerId);
}

const byPosition = (a, b) => (a.position || 0) - (b.position || 0) || String(a.created_at).localeCompare(String(b.created_at));

// Every local pack with its stickers (sorted), each sticker with an object URL for display.
export async function listLocalPacks() {
  const [packs, stickers] = await Promise.all([getAll(PACKS), getAll(STICKERS)]);
  const byPack = new Map();
  (stickers || []).forEach((s) => {
    if (!byPack.has(s.pack_id)) byPack.set(s.pack_id, []);
    byPack.get(s.pack_id).push(s);
  });
  return (packs || []).sort(byPosition).map((p) => ({
    ...p,
    stickers: (byPack.get(p.id) || []).sort(byPosition).map((s) => ({ ...s, url: urlFor(s) })),
  }));
}

export async function getLocalSticker(id) {
  return getOne(STICKERS, id);
}

export async function createLocalPack({ name, description = '' }) {
  const clean = String(name || '').trim().slice(0, 40);
  if (clean.length < 2) throw new Error('Pack name must be at least 2 characters');
  const existing = (await getAll(PACKS)) || [];
  if (existing.length >= LOCAL_LIMITS.maxPacks) throw new Error(`You can keep at most ${LOCAL_LIMITS.maxPacks} local packs on this device`);
  const now = new Date().toISOString();
  const pack = { id: newId('lp'), name: clean, description: String(description || '').trim().slice(0, 200), cover_sticker_id: '', position: Date.now(), created_at: now, updated_at: now, server_pack_id: 0, moved_at: '' };
  await putOne(PACKS, pack);
  changed();
  return pack;
}

export async function updateLocalPack(id, patch) {
  const cur = await getOne(PACKS, id);
  if (!cur) throw new Error('That local pack no longer exists');
  const next = { ...cur, ...patch, id, updated_at: new Date().toISOString() };
  if (patch.name != null) {
    next.name = String(patch.name).trim().slice(0, 40);
    if (next.name.length < 2) throw new Error('Pack name must be at least 2 characters');
  }
  if (patch.description != null) next.description = String(patch.description).trim().slice(0, 200);
  await putOne(PACKS, next);
  changed();
  return next;
}

// Removes the pack and its stickers from this device. Server copies (after a move) are not affected.
export async function deleteLocalPack(id) {
  const stickers = ((await getAll(STICKERS)) || []).filter((s) => s.pack_id === id);
  for (const s of stickers) {
    await removeOne(STICKERS, s.id);
    forget(s.id);
  }
  await removeOne(PACKS, id);
  changed();
}

export async function addLocalSticker(packId, { name, keywords = [], blob, mime, animated = false }) {
  const pack = await getOne(PACKS, packId);
  if (!pack) throw new Error('That local pack no longer exists');
  if (!blob || !blob.size) throw new Error('No image');
  if (blob.size > LOCAL_LIMITS.maxBytes) throw new Error('This image is too large (max 2 MB after resizing)');
  const type = String(mime || blob.type || '').toLowerCase();
  if (!/^image\/(png|jpeg|gif|webp)$/.test(type)) throw new Error('Only PNG, JPEG, GIF or WebP images');
  const inPack = ((await getAll(STICKERS)) || []).filter((s) => s.pack_id === packId);
  if (inPack.length >= LOCAL_LIMITS.maxStickersPerPack) throw new Error(`A pack can hold at most ${LOCAL_LIMITS.maxStickersPerPack} stickers`);
  const now = new Date().toISOString();
  const sticker = {
    id: newId('ls'),
    pack_id: packId,
    name: String(name || 'sticker').trim().slice(0, 40) || 'sticker',
    keywords: Array.from(new Set((keywords || []).map(String))).slice(0, 20),
    blob,
    mime: type,
    animated: !!animated,
    favorite: false,
    position: inPack.reduce((m, s) => Math.max(m, s.position || 0), 0) + 1,
    created_at: now,
    updated_at: now,
    relay: null,
  };
  await putOne(STICKERS, sticker);
  if (!pack.cover_sticker_id) await putOne(PACKS, { ...pack, cover_sticker_id: sticker.id, updated_at: now });
  changed();
  return sticker;
}

// silent: bookkeeping that should not refresh sticker lists (relay ids).
export async function updateLocalSticker(id, patch, { silent = false } = {}) {
  const cur = await getOne(STICKERS, id);
  if (!cur) throw new Error('That sticker no longer exists');
  const next = { ...cur, ...patch, id, pack_id: cur.pack_id, updated_at: new Date().toISOString() };
  if (patch.name != null) next.name = String(patch.name).trim().slice(0, 40) || 'sticker';
  if (patch.keywords != null) next.keywords = Array.from(new Set(patch.keywords.map(String))).slice(0, 20);
  await putOne(STICKERS, next);
  if (!silent) changed();
  return next;
}

export async function deleteLocalSticker(id) {
  const cur = await getOne(STICKERS, id);
  if (!cur) return;
  await removeOne(STICKERS, id);
  forget(id);
  const pack = await getOne(PACKS, cur.pack_id);
  if (pack && pack.cover_sticker_id === id) {
    const rest = ((await getAll(STICKERS)) || []).filter((s) => s.pack_id === pack.id).sort(byPosition);
    await putOne(PACKS, { ...pack, cover_sticker_id: rest[0] ? rest[0].id : '' });
  }
  changed();
}

export async function reorderLocalStickers(packId, ids) {
  let pos = 0;
  for (const id of ids) {
    const cur = await getOne(STICKERS, id);
    if (!cur || cur.pack_id !== packId) continue;
    pos += 1;
    await putOne(STICKERS, { ...cur, position: pos });
  }
  changed();
}

// Local packs in the shape of /stickers/usable packs, for the composer (picker and keyword suggestions).
// Local stickers are personal: in server channels they need Use external stickers, like personal packs.
export async function localUsablePacks() {
  const packs = await listLocalPacks().catch(() => []);
  // A pack moved to Voxemera Server is used from there; a kept local copy would show every sticker twice.
  return packs
    .filter((p) => p.stickers.length && !p.server_pack_id)
    .map((p) => ({
      id: `${LOCAL_ID_PREFIX}${p.id}`,
      local: true,
      localId: p.id,
      name: p.name,
      visibility: 'local',
      via: 'personal',
      icon_url: (p.stickers.find((s) => s.id === p.cover_sticker_id) || p.stickers[0]).url,
      stickers: p.stickers.map((s) => ({
        id: `${LOCAL_ID_PREFIX}${s.id}`,
        localId: s.id,
        local: true,
        name: s.name,
        url: s.url,
        keywords: s.keywords || [],
        favorite: !!s.favorite,
        animated: !!s.animated,
      })),
    }));
}

// For tests.
export function resetLocalStickerMemory() {
  memory[PACKS].clear();
  memory[STICKERS].clear();
  urls.forEach((u) => URL.revokeObjectURL && URL.revokeObjectURL(u.url));
  urls.clear();
}
